import React, { useState } from "react";
import { MapPin } from "lucide-react";
import { useT } from "../../lib/lang";

const ANNOTATION_COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#a855f7", "#ec4899"];

// Iter99 — Phase 2C (spec §4.1): the form shown right after a single
// tap places a Point marker. The marker is NOT added to the session
// FeatureCollection (and therefore never historised) until "Enregistrer"
// is pressed — see planificationDraw.js's `onAnnotationDrafted` routing
// and PlanificationTab.jsx's handling. Clicking "Annuler" here calls
// `draw.removeFeatures([id])`, leaving no trace anywhere (spec: "une
// annotation non validée ne doit pas être ajoutée à l'historique").
export const PlanificationAnnotationForm = ({ coordinates, onSave, onCancel }) => {
  const [t] = useT();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(ANNOTATION_COLORS[0]);
  const [error, setError] = useState("");

  const handleSave = () => {
    const trimmed = title.trim();
    if (!trimmed) { setError(t.planificationAnnotationTitleRequired); return; }
    onSave({ title: trimmed, description: description.trim(), color });
  };

  return (
    <div
      className="absolute inset-0 z-[480] flex items-end sm:items-center justify-center bg-black/50 p-4"
      data-testid="planification-annotation-form-backdrop"
    >
      <div
        className="w-full sm:w-auto sm:min-w-[22rem] bg-[#111418] border border-zinc-700 p-4 flex flex-col gap-3"
        data-testid="planification-annotation-form"
      >
        <div className="flex items-center gap-2 text-emerald-300">
          <MapPin className="w-4 h-4 shrink-0" />
          <span className="text-sm font-semibold">{t.planificationAnnotationNewTitle}</span>
        </div>

        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          {t.planificationAnnotationTitleField} *
          <input
            type="text"
            autoFocus
            data-testid="planification-annotation-title-input"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (error) setError(""); }}
            className="bg-[#0b0f14] border border-zinc-700 px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500"
          />
          {error && <span className="text-red-400" data-testid="planification-annotation-title-error">{error}</span>}
        </label>

        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          {t.planificationDescriptionField}
          <textarea
            rows={2}
            data-testid="planification-annotation-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="bg-[#0b0f14] border border-zinc-700 px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500 resize-none"
          />
        </label>

        <div className="flex flex-col gap-1 text-xs text-zinc-400">
          {t.planificationColorField}
          <div className="flex items-center gap-2" data-testid="planification-annotation-color-swatches">
            {ANNOTATION_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`planification-annotation-color-${c.slice(1)}`}
                onClick={() => setColor(c)}
                aria-label={c}
                aria-pressed={color === c}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? "border-white scale-110" : "border-transparent"}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1 text-xs text-zinc-400">
          {t.planificationGpsCoordinates}
          <span className="font-mono text-zinc-300" data-testid="planification-annotation-gps-readonly">
            {coordinates ? `${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}` : "—"}
          </span>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            data-testid="planification-annotation-cancel-btn"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-medium text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-colors"
          >
            {t.planificationCancelAction}
          </button>
          <button
            type="button"
            data-testid="planification-annotation-save-btn"
            onClick={handleSave}
            className="px-3 py-1.5 text-xs font-semibold text-black bg-emerald-500 hover:bg-emerald-400 transition-colors"
          >
            {t.planificationSave}
          </button>
        </div>
      </div>
    </div>
  );
};
